import './perks-nav.component.scss';

import type { FunctionComponent } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useHashLocation } from 'wouter-preact/use-hash-location';

import { IconBars } from '../../../common/icons/icon-bars';
import { IconCaret } from '../../../common/icons/icon-caret';
import { IconEllipsisClose } from '../../../common/icons/icon-ellipsis-close/icon-ellipsis-close';
import { Pageable } from '../../../common/page/pageable.model';
import { useTranslation } from '../../../i18n';
import { selectMemberships } from '../../../membership/store/membership.selectors';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getSearchParams } from '../../../utils/window.utils';
import type { PostFilter } from '../../model/post-filter.model';
import type { ContentTab } from '../../model/tab.interface';
import { ContentType } from '../../model/tab.interface';
import { PerksHelper } from '../../perks.helper';
import { selectContentTabs } from '../../store/perks.selectors';
import { loadPosts } from '../../store/perks.thunk';
import { getMobileStack } from './mobile-stack';
import { PerksMyAcctNavLink } from './perks-my-acct-nav-link';
import { PerksNavLinks } from './perks-nav-links';
import { PerksNavMobileLinks } from './perks-nav-mobile-links.component';

export interface Props {
  hasActiveFilters: boolean;
  /** the feed's active filter, so reloading from the Posts tab keeps what the filter bar shows */
  postFilter: PostFilter;
}

interface OverFlowNav {
  showMoreButton: boolean;
  displayNavOverflow: boolean;
  overflowItemSelected: boolean;
  maxDisplayItems: number;
}

export const PerksNav: FunctionComponent<Props> = ({ hasActiveFilters, postFilter }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [location] = useHashLocation();
  const memberships = useAppSelector(selectMemberships);
  const tabs = useAppSelector(selectContentTabs);
  const sortedTabs = [...tabs].sort((a, b) => a.order - b.order);
  const otaMode = !!getSearchParams()?.get('access');

  const handleTabClick = (tab: ContentTab) => {
    if (tab.contentType === ContentType.POST) {
      dispatch(
        loadPosts({
          pageable: Pageable.of(
            0,
            PerksHelper.POSTS_DEFAULT_PAGE_SIZE,
            PerksHelper.POSTS_DEFAULT_SORT
          ),
          filter: postFilter,
        })
      );
    }
    // Paginated content types (VIDEO, ALBUM, PRODUCT, etc.) are loaded by the
    // location-change effect in perks.component.tsx when the route updates.
  };
  const [overFlowNav, setOverFlowNav] = useState<OverFlowNav>({
    showMoreButton: false,
    displayNavOverflow: false,
    overflowItemSelected: false,
    maxDisplayItems: 7,
  });
  const mobileNavRef = useRef<any>(null);

  const toggleNavOverflow = () => {
    setOverFlowNav((prev) => ({
      ...prev,
      displayNavOverflow: !prev.displayNavOverflow,
    }));
  };

  const closeNavOverflow = () => {
    setOverFlowNav((prev) => ({
      ...prev,
      displayNavOverflow: false,
    }));
  };

  useEffect(() => {
    const stack = getMobileStack();
    if (!stack) return;
    stack.classList.toggle('overflow-open', overFlowNav.displayNavOverflow);
  }, [overFlowNav.displayNavOverflow]);

  const updateShowMoreButton = (maxItems?: number): void => {
    const maxDisplayItems = maxItems ?? overFlowNav.maxDisplayItems;
    const isDesktop = window.innerWidth > 768;
    // Select only the main nav links, not the overflow dropdown
    const selector = isDesktop
      ? '.desktop-nav > ul > .desktop-nav-link'
      : '.mobile-nav-link:not(.my-account)';
    const container = isDesktop ? document.querySelector('.desktop-nav') : mobileNavRef.current;
    const links: NodeList = container?.querySelectorAll(selector) || [];

    const newShowMoreState = links.length > maxDisplayItems;
    const selectedItem = Array.from(links).findIndex((link) =>
      (link as HTMLElement).classList.contains('active')
    );

    // Check if my-account is active separately (it's always visible on desktop, in overflow on mobile)
    const myAccountLink = container?.querySelector('.my-account');
    const isMyAccountActive = myAccountLink?.classList.contains('active');

    // When More button is shown, it takes a slot
    // On desktop: My Account also takes a slot (always visible), so subtract 2
    // On mobile: My Account is in overflow, so only subtract 1
    const slotsToSubtract = isDesktop ? 2 : 1;
    const visibleNavItems = newShowMoreState ? maxDisplayItems - slotsToSubtract : maxDisplayItems;
    const overflowItemSelectedState =
      selectedItem >= 0 && selectedItem >= visibleNavItems && !isMyAccountActive;

    if (
      overFlowNav.showMoreButton !== newShowMoreState ||
      overFlowNav.overflowItemSelected !== overflowItemSelectedState
    ) {
      setOverFlowNav((prev) => ({
        ...prev,
        showMoreButton: newShowMoreState,
        overflowItemSelected: overflowItemSelectedState,
      }));
    }
  };

  const checkScreenSize = () => {
    let maxDisplayItems: number;
    if (window.innerWidth > 1000) {
      maxDisplayItems = 7;
    } else if (window.innerWidth > 768) {
      maxDisplayItems = 6;
    } else {
      maxDisplayItems = 5;
    }

    setOverFlowNav((prev) => ({
      ...prev,
      maxDisplayItems,
    }));

    updateShowMoreButton(maxDisplayItems);
  };

  useEffect(() => {
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  // Update active state when location changes
  useEffect(() => {
    // Small delay to ensure DOM has updated with new active classes
    setTimeout(() => updateShowMoreButton(), 0);
  }, [location]);

  // Hide nav in OTA mode (access code link) regardless of membership state
  if (otaMode) return null;

  return (
    <div className="perks-nav-container">
      <nav
        aria-label={t('a11y.communityNav')}
        className={`desktop-nav ${overFlowNav.showMoreButton ? 'show-more' : ''}`}
      >
        <ul>
          <PerksNavLinks
            hasActiveFilters={hasActiveFilters}
            onCloseNavOverflow={closeNavOverflow}
            onTabClick={handleTabClick}
            memberships={memberships ?? []}
            tabs={sortedTabs}
          />

          {overFlowNav.showMoreButton && (
            <li
              className={`desktop-nav-more ${overFlowNav.overflowItemSelected ? 'active' : ''}`}
              onClick={toggleNavOverflow}
            >
              <a
                role="button"
                tabIndex={0}
                aria-haspopup="true"
                aria-expanded={overFlowNav.displayNavOverflow}
                onKeyDown={(e: KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleNavOverflow();
                  }
                }}
              >
                <span class="h5 single-h5">{t('perks.more')}</span>
                <IconCaret />
              </a>
              {overFlowNav.displayNavOverflow && (
                <div className="desktop-nav-overflow">
                  <ul>
                    <PerksNavLinks
                      hasActiveFilters={hasActiveFilters}
                      onCloseNavOverflow={closeNavOverflow}
                      onTabClick={handleTabClick}
                      memberships={memberships ?? []}
                      tabs={sortedTabs}
                    />
                  </ul>
                </div>
              )}
            </li>
          )}

          <PerksMyAcctNavLink onClick={closeNavOverflow} />
        </ul>
      </nav>

      <>
        {(() => {
          const stack = getMobileStack();
          const mobileNavEl = (
            <div
              class={`mobile-nav ${overFlowNav.showMoreButton ? 'show-more' : ''}`}
              role="navigation"
              aria-label={t('a11y.communityNav')}
              data-mobile-footer
              ref={mobileNavRef}
            >
              <PerksNavMobileLinks
                hasActiveFilters={hasActiveFilters}
                onCloseNavOverflow={closeNavOverflow}
                onTabClick={handleTabClick}
                tabs={sortedTabs}
              />

              {overFlowNav.showMoreButton && (
                <div
                  class={`mobile-nav-more ${overFlowNav.overflowItemSelected ? 'active' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-haspopup="true"
                  aria-expanded={overFlowNav.displayNavOverflow}
                  onClick={toggleNavOverflow}
                  onKeyDown={(e: KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleNavOverflow();
                    }
                  }}
                >
                  <IconEllipsisClose
                    open={overFlowNav.displayNavOverflow}
                    width={25}
                    height={25}
                    fill={overFlowNav.overflowItemSelected ? '#ffffff' : undefined}
                  />
                  <span class="mobile-nav-link-title">{t('perks.more')}</span>
                </div>
              )}
            </div>
          );

          return stack ? createPortal(mobileNavEl, stack) : mobileNavEl;
        })()}

        {(() => {
          const stack = getMobileStack();
          const overflowEl = (
            <div
              class={`mobile-nav-overflow${overFlowNav.displayNavOverflow ? ' is-open' : ''}`}
              data-attach-to-mobile-footer
            >
              <PerksNavMobileLinks
                hasActiveFilters={hasActiveFilters}
                onCloseNavOverflow={closeNavOverflow}
                onTabClick={handleTabClick}
                tabs={sortedTabs}
              />
            </div>
          );
          return stack ? createPortal(overflowEl, stack) : overflowEl;
        })()}
      </>
    </div>
  );
};
